<script setup lang="ts">
import { Plus } from 'lucide-vue-next'
import EmptyState from '@/components/EmptyState.vue'
import Forbidden402Alt from '@/components/errors/402Alt.vue'
import axios from 'axios'

import ContestList from './Components/ContestList.vue'
import ContestTemplateModal from './Components/ContestTemplateModal.vue'
import ContestSuccessConfig from './Components/ContestSuccessConfig.vue'
import useContentScopeList, { useContentScopeCriteria, useContentScopeSearch } from '@/composables/contentScopeList'
import type { ContentCreator, ContentScope } from '@/types/contentScope'
import { contentScopeListParams, splitContentScopeListParams } from '@/utils/contentScope'
import { storeParams } from '@/utils/storeParams'

export type ContestGame = {
  id: string
  name: string
  isActive: boolean
  isPaused: number
  isLegacy: boolean
  participantsCounts: number
  stores: Array<{
    name: string
  }>
  contestGameBoostType: string
  fromDate: string
  toDate: string
  shortLink: string
  rewards?: Array<{
    id: number
    name: string
  }>
  isVisibleInWebapp?: boolean
  scope?: ContentScope | null
  creator?: ContentCreator | null
}

const { permissions, stores } = useLocalStorage()
const { t } = useI18n()
const { fetchTemplates } = useTemplate()
const notification = useNotification()
const { objectiveSlug, selectedStoreFilter } = useStore()
const router = useRouter()

const isSuccessConfigVisible = computed(
  () => objectiveSlug.value === 'google' && stores.value.every((store) => !store.googlePlaceId)
)

const currentPage = ref(1)
const pageSize = 10
const isFetching = ref(false)
const totalPage = ref(1)
const lastPage = ref(1)
const isLoading = ref(false)
const storeFilter = ref<number[]>(selectedStoreFilter.value.length ? [...selectedStoreFilter.value] : [])
const contests = ref<ContestGame[]>([])
const templates = ref([])

const isButtonLoading = ref(false)

const pageTitle = {
  title: 'pages.contests.contestsPage.title'
}

const showContestCreateButton = computed(() => permissions.value['contest-game.create'])

const isTemplateTipsOpen = ref(false)
const hasFetchedTemplates = ref(false)

const openTemplatesTips = async () => {
  if (!hasFetchedTemplates.value) {
    await fetchOnceTemplates()
  }
  isTemplateTipsOpen.value = true
}

const fetchOnceTemplates = async () => {
  templates.value = await fetchTemplates('contests')
  hasFetchedTemplates.value = true
}

const hasNextPage = ref(false)
const hasPrevPage = ref(false)

const contestListParams = (params: Record<string, unknown>) => {
  const { search, scopeParams } = splitContentScopeListParams(params)

  return {
    ...storeParams(storeFilter.value, stores.value),
    ...scopeParams,
    ...(search && { search })
  }
}

const fetchContestsTotal = async (scopeParams: Record<string, unknown>) => {
  const { data } = await axios.get<{ meta: { total: number } }>(`contest-games`, {
    params: { page: 1, per_page: 1, ...contestListParams(scopeParams) }
  })
  return data.meta.total
}

const contentScope = useContentScopeList('contest_game', {
  fetchTotal: fetchContestsTotal,
  reload: () => fetchData(),
  countsKey: () => storeFilter.value
})

const { origin, createdBy } = useContentScopeCriteria(contentScope)
const filterCriteria = computed(() => [origin.value, createdBy.value])
const { searchText } = useContentScopeSearch(contentScope)

const hasListContent = computed(
  () => contests.value.length > 0 || contentScope.hasActiveFilters.value || storeFilter.value.length > 0
)

const fetchData = async (page = 1) => {
  currentPage.value = page
  isFetching.value = true
  try {
    const urlParams = {
      page: currentPage.value,
      per_page: pageSize,
      ...storeParams(storeFilter.value, stores.value),
      ...contentScopeListParams(contentScope.filters.value)
    }

    const { data } = await axios.get<{
      data: ContestGame[]
      meta: { last_page: number; total: number }
    }>(`contest-games`, { params: urlParams })

    contests.value = data.data
    totalPage.value = data.meta.total
    lastPage.value = data.meta.last_page
    hasNextPage.value = data.meta.last_page > page
    hasPrevPage.value = page > 1
    isFetching.value = false
    return data.meta.total > page * pageSize
  } catch {
    return false
  }
}

const saveGooglePage = async (e: string, storeId: number) => {
  try {
    const { data } = await axios.patch(`stores/${storeId}/google-place-id`, {
      googlePlaceId: e
    })
    if (data.data.googlePlaceId) {
      notification.create('success', {
        text: 'Votre fiche google a bien été enregistrée'
      })
      const { data: storesData } = await axios.get('stores')
      stores.value = storesData.data
    }
  } catch {
    notification.create('error', {
      text: "Une erreur est survenue dans l'enregistrement de votre fiche Google"
    })
  } finally {
    isButtonLoading.value = false
    router.push({ name: 'ContestCreate', query: { isNew: 'true' } })
  }
}

const handleSubmitGooglePage = (e: string) => {
  isButtonLoading.value = true
  if (e) {
    const storeId = stores.value[0].id
    saveGooglePage(e, storeId)
    return
  }

  if (!stores.value[0].googlePlaceId && !e) {
    notification.create('error', {
      text: 'Vous devez renseigner votre fiche google pour pouvoir créer un jeu concours'
    })
    isButtonLoading.value = false
  } else {
    isButtonLoading.value = false
    router.push({ name: 'ContestCreate' })
  }
}

const urlParams = computed(() => {
  let params = {
    isNew: (contests.value.length === 0).toString()
  } as Record<string, string>

  const hasWebAppActivated = contests.value.some((contest) => contest.isVisibleInWebapp)
  if (!hasWebAppActivated) {
    params = {
      ...params,
      activeInWebApp: 'true'
    }
  }
  return params
})
const { hasError } = useError()

watch(storeFilter, async (newValue) => {
  selectedStoreFilter.value = [...newValue]
  await fetchData()
})

onMounted(async () => {
  isLoading.value = true
  await fetchData()
  isLoading.value = false
})
</script>

<template>
  <div>
    <TheLoadingPage v-if="isLoading" />
    <div v-else>
      <Forbidden402Alt v-if="hasError" module="contests" @open-templates-tips="openTemplatesTips" />
      <ContestSuccessConfig
        v-else-if="isSuccessConfigVisible"
        :is-button-loading="isButtonLoading"
        @submit-google-page="handleSubmitGooglePage($event)"
      />
      <div v-else>
        <TheListHeader :title="t(pageTitle.title)">
          <template #actions>
            <Button
              v-if="contests.length > 0 && showContestCreateButton"
              variant="default-primary"
              @click="openTemplatesTips"
            >
              <Plus />
              {{ t('pages.contests.contestsPage.cta.create') }}
            </Button>
          </template>
        </TheListHeader>
        <div class="px-6 pb-6 sm:px-12 sm:pb-12">
          <div v-if="hasListContent">
            <ListToolbar class="mb-4" :criteria="filterCriteria">
              <template #leading>
                <ListSearchInput v-model="searchText" :placeholder="t('contentScope.filters.search')" />
                <MultiSelectStores v-model="storeFilter" :has-label="false" />
              </template>
            </ListToolbar>
            <ContentScopeEmptyState
              v-if="!isFetching && contests.length === 0"
              :is-mine-view="contentScope.isMineView.value"
              :subject="contentScope.subject"
              @reset="contentScope.resetFilters()"
            />
            <ContestList
              v-else
              :contests="contests"
              with-origin
              :table-title="null"
              :total="totalPage"
              :current-page="currentPage"
              :last-page="lastPage"
              :is-fetching="isFetching"
              :page-size="pageSize"
              :has-next-page="hasNextPage"
              :has-prev-page="hasPrevPage"
              show-empty-state
              :empty-state-message="t('pages.contests.contestsPage.tabs.new.emptyState.title')"
              @change-page="fetchData"
            />
          </div>

          <EmptyState v-else :url-params="urlParams" :module="'contests'" />
        </div>
      </div>
    </div>
  </div>

  <ContestTemplateModal
    :is-open="isTemplateTipsOpen"
    :templates="templates"
    :url-params="urlParams"
    @close="isTemplateTipsOpen = false"
  />
</template>
